import type { DoorSignIn } from '../../app/router/routes'

/**
 * The staff sign-in's outcomes, plus the one P3-16 added.
 *
 * 🔴 `change-password` IS NOT A SIGN-IN. The password was right and somebody
 * else chose it (`User.mustChangePassword` — an admin-set teacher password, a
 * Kalqy temporary password). No session exists yet; the router holds a
 * purpose-scoped token and the screen asks for a new password, after which the
 * sign-in continues as `DoorSignIn` does (signed in, wrong door, or the second
 * factor).
 *
 * ⚠️ DECLARED HERE RATHER THAN BESIDE `DoorSignIn` IN `routes.ts`, which is frozen
 * for this release. It widens that type and never narrows it, so every screen
 * written against `DoorSignIn` still compiles against this.
 */
export type StaffSignIn = DoorSignIn | { at: 'change-password' }

/**
 * How a refused request on a set-password form reads — the forced change, the
 * reset link and the invite claim share it.
 *
 * ⚠️ 400 IS THE PASSWORD RULE AND NOTHING ELSE. 401/403 is one generic refusal
 * (an expired step, a used link) and the screen must not try to tell which.
 * 0 is "no answer at all" (`client.ts`), separated so somebody on a dropped
 * connection is not told their link is dead.
 */
export type PasswordFormFailure = 'rule' | 'refused' | 'throttled' | 'server-fault' | 'failed'

export function passwordFormFailure(status: number | undefined): PasswordFormFailure {
  if (status === 400) return 'rule'
  if (status === 401 || status === 403) return 'refused'
  if (status === 429) return 'throttled'
  if (typeof status === 'number' && status >= 500) return 'server-fault'
  return 'failed'
}

/**
 * The forgot-password form's only gate: something shaped like an address.
 *
 * 🔴 A SHAPE CHECK, NEVER A LOOKUP. The server answers the same 200 for every
 * address, and a client that refused some addresses for any other reason would
 * be rebuilding the staff directory the generic answer exists to hide.
 */
export function canRequestReset(email: string): boolean {
  const trimmed = email.trim()
  if (trimmed.length < 3 || trimmed.length > 254) return false
  const at = trimmed.indexOf('@')
  return at > 0 && at === trimmed.lastIndexOf('@') && trimmed.indexOf('.', at) > at + 1 && !trimmed.endsWith('.')
}

/**
 * P0-10 — a write refused because the session is an operator's read-only
 * "View as school". `ApiError.code` carries the server's slug.
 */
export const IMPERSONATION_READ_ONLY = 'IMPERSONATION_READ_ONLY'

export const IMPERSONATION_READ_ONLY_MESSAGE = 'You are viewing as a school — changes are switched off.'

export function isImpersonationReadOnly(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === IMPERSONATION_READ_ONLY
  )
}
